import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import UserService from "../../../services/UserService";
import OutilService, { OUTIL_ERROR_MESSAGES } from "../../../services/smq/OutilService";
import { fieldSx, OUTIL_COLORS, paperSx } from "./OutilContainer.styles";

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const EMPTY_FORM = {
  approbateur_id: "",
  date_redaction: "",
  outil_code: "",
  redacteur_id: "",
  verificateur_id: "",
  version: "",
};

const sameId = (left, right) =>
  left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right);

const dateInputValue = (value) => String(value || "").slice(0, 10);

const fileName = (path) => {
  const name = String(path || "").split(/[\\/]/).pop() || "";
  return name.replace(/^[a-f0-9]{24}__/, "") || "Pièce jointe";
};

const userLabel = (user) => {
  const fullName = [user?.first_name, user?.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  const name = String(user?.name || user?.identity || "").trim() || fullName || "Utilisateur sans nom";
  const fonction = String(user?.function_name || user?.fonction || user?.function || "").trim();
  return [name, fonction].filter(Boolean).join(" — ");
};

const normalizeUsers = (payload) => {
  if (!Array.isArray(payload?.data)) throw new Error(OUTIL_ERROR_MESSAGES.fetch);
  return payload.data
    .map((user) => ({ id: user?.user_id, label: userLabel(user) }))
    .filter((user) => user.id !== null && user.id !== undefined)
    .sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
};

const triggerDownload = (blob, name) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const FieldLabel = ({ children }) => (
  <Typography
    color={OUTIL_COLORS.textMuted}
    component="label"
    fontSize={9.5}
    fontWeight={700}
    letterSpacing="0.025em"
    sx={{ display: "block", mb: 0.4, textTransform: "uppercase" }}
  >
    {children}
  </Typography>
);

const MetadataField = ({ label, ...props }) => (
  <Box sx={{ minWidth: 0 }}>
    <FieldLabel>{label}</FieldLabel>
    <TextField
      fullWidth
      size="small"
      sx={{
        ...fieldSx,
        "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 11.5, height: 32 },
        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#31465d" },
      }}
      {...props}
    />
  </Box>
);

const UserField = ({ disabled, fallbackLabel, label, onChange, options, value }) => {
  const selected = options.find((option) => sameId(option.id, value)) ||
    (value ? { id: value, label: fallbackLabel || "Utilisateur non disponible" } : null);

  return (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel>{label}</FieldLabel>
      <Autocomplete
        disabled={disabled}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, current) => sameId(option.id, current.id)}
        onChange={(_, option) => onChange(option?.id ?? "")}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Sélectionner un utilisateur"
            size="small"
            sx={{
              ...fieldSx,
              "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 11.5, minHeight: 32, py: 0 },
              "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#31465d" },
            }}
          />
        )}
        value={selected}
      />
    </Box>
  );
};

const OutilEntryPanel = ({ onEntryChange, processus }) => {
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [users, setUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [entryError, setEntryError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState({ severity: "success", text: "" });
  const fileInputRef = useRef(null);
  const entryRequestRef = useRef(0);
  const usersRequestRef = useRef(0);

  const applyEntry = useCallback((nextEntry) => {
    setEntry(nextEntry || null);
    setForm({
      approbateur_id: nextEntry?.validator_id ?? "",
      date_redaction: dateInputValue(nextEntry?.created_at),
      outil_code: nextEntry?.outil_code || "",
      redacteur_id: nextEntry?.creator_id ?? "",
      verificateur_id: nextEntry?.auditor_id ?? "",
      version: nextEntry?.version === null || nextEntry?.version === undefined ? "" : String(nextEntry.version),
    });
    setFile(null);
    onEntryChange(nextEntry || null);
  }, [onEntryChange]);

  const loadEntry = useCallback(async () => {
    const requestId = entryRequestRef.current + 1;
    entryRequestRef.current = requestId;
    setLoadingEntry(true);
    setEntryError("");
    try {
      const response = await OutilService.getEntree(processus.processus_id);
      if (requestId === entryRequestRef.current) applyEntry(response?.data || null);
    } catch (error) {
      if (requestId === entryRequestRef.current) {
        applyEntry(null);
        setEntryError(OUTIL_ERROR_MESSAGES.fetch);
      }
    } finally {
      if (requestId === entryRequestRef.current) setLoadingEntry(false);
    }
  }, [applyEntry, processus.processus_id]);

  const loadUsers = useCallback(async () => {
    const requestId = usersRequestRef.current + 1;
    usersRequestRef.current = requestId;
    setLoadingUsers(true);
    setUsersError("");
    try {
      const options = normalizeUsers(await UserService.getAllUserActive());
      if (requestId === usersRequestRef.current) setUsers(options);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("[OutilEntryPanel users]", error);
      if (requestId === usersRequestRef.current) {
        setUsers([]);
        setUsersError(OUTIL_ERROR_MESSAGES.fetch);
      }
    } finally {
      if (requestId === usersRequestRef.current) setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    setEditing(false);
    setMessage({ severity: "success", text: "" });
    onEntryChange(null);
    loadEntry();
    loadUsers();
    return () => {
      entryRequestRef.current += 1;
      usersRequestRef.current += 1;
    };
  }, [loadEntry, loadUsers, onEntryChange]);

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const cancel = () => {
    applyEntry(entry);
    setEditing(false);
    setMessage({ severity: "success", text: "" });
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    event.target.value = "";
    if (selected && selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setMessage({ severity: "error", text: "La pièce jointe ne doit pas dépasser 20 Mo." });
      return;
    }
    setFile(selected);
    setMessage({ severity: "success", text: "" });
  };

  const save = async () => {
    if (saving) return;
    if (form.version && !/^-?\d+$/.test(String(form.version))) {
      setMessage({ severity: "error", text: "La version doit être un nombre entier." });
      return;
    }

    setSaving(true);
    setMessage({ severity: "success", text: "" });
    try {
      const data = new FormData();
      data.append("processus_id", processus.processus_id);
      Object.entries(form).forEach(([key, value]) => data.append(key, value ?? ""));
      if (file) data.append("file", file);
      const response = entry
        ? await OutilService.updateEntree(entry.outil_entree_id, data)
        : await OutilService.createEntree(data);
      applyEntry(response.data);
      setEditing(false);
      setMessage({ severity: "success", text: "Fiche documentaire enregistrée." });
    } catch (error) {
      setMessage({ severity: "error", text: OUTIL_ERROR_MESSAGES.save });
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (!entry?.outil_entree_id || downloading) return;
    setDownloading(true);
    try {
      const response = await OutilService.downloadFile(entry.outil_entree_id);
      triggerDownload(response.data, fileName(entry.file));
    } catch (error) {
      setMessage({ severity: "error", text: OUTIL_ERROR_MESSAGES.download });
    } finally {
      setDownloading(false);
    }
  };

  const userFallbacks = useMemo(() => ({
    approbateur_id: entry?.validator_name,
    redacteur_id: entry?.creator_name,
    verificateur_id: entry?.auditor_name,
  }), [entry]);

  const busy = saving || loadingEntry;

  return (
    <Paper aria-label="Fiche documentaire du processus" elevation={0} role="region" sx={{ ...paperSx, borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          alignItems: { xs: "stretch", md: "center" },
          backgroundColor: "#edf2f8",
          borderBottom: `1px solid ${OUTIL_COLORS.border}`,
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "minmax(250px, auto) minmax(340px, 550px) 1fr" },
          minHeight: 100,
          px: 2,
          py: 1.75,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={800}>
            <AttachFileOutlinedIcon sx={{ color: "#bf7c48", fontSize: 14, mr: 0.65, verticalAlign: "middle" }} />
            Fiche documentaire du processus
          </Typography>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10} sx={{ mt: 0.4 }}>
            {processus.instance || "Processus sélectionné"}
          </Typography>
        </Box>

        <Button
          disabled={busy || downloading || (!editing && !entry?.file)}
          onClick={editing ? () => fileInputRef.current?.click() : download}
          startIcon={downloading ? <CircularProgress size={18} /> : editing ? <AttachFileOutlinedIcon /> : <DownloadOutlinedIcon />}
          sx={{ border: "1px dashed #a9c1d8", borderRadius: 1.5, color: OUTIL_COLORS.navy, justifyContent: "flex-start", minHeight: 60, px: 2, textAlign: "left", textTransform: "none" }}
        >
          <Box>
            <Typography fontSize={11.5} fontWeight={800}>
              {editing ? (entry?.file ? "Remplacer la pièce jointe" : "Importer une pièce jointe") : (entry?.file ? fileName(entry.file) : "Aucune pièce jointe")}
            </Typography>
            <Typography fontSize={9.5} fontWeight={400} sx={{ mt: 0.25 }}>
              {file ? file.name : editing ? "Tous formats acceptés · Taille maximale : 20 Mo." : entry?.file ? "Cliquer pour télécharger." : "Cliquer sur Modifier pour ajouter un fichier."}
            </Typography>
          </Box>
        </Button>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: { md: "flex-end" } }}>
          {!editing ? (
            <Button disabled={busy} onClick={() => setEditing(true)} size="small" startIcon={<EditOutlinedIcon />} variant="contained">Modifier</Button>
          ) : (
            <>
              <Button disabled={saving} onClick={cancel} size="small">Annuler</Button>
              <Button disabled={saving} onClick={save} size="small" startIcon={saving ? <CircularProgress color="inherit" size={15} /> : <SaveOutlinedIcon />} variant="contained">Enregistrer</Button>
            </>
          )}
        </Box>
      </Box>

      {entryError && <Alert action={<Button color="inherit" onClick={loadEntry} size="small">Réessayer</Button>} severity="error">{entryError}</Alert>}
      {usersError && <Alert action={<Button color="inherit" onClick={loadUsers} size="small">Réessayer</Button>} severity="error">{usersError}</Alert>}
      {message.text && <Alert severity={message.severity} sx={{ borderRadius: 0, py: 0 }}>{message.text}</Alert>}

      <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" }, px: 1.75, py: 1.5 }}>
        <MetadataField disabled={!editing || saving} inputProps={{ maxLength: 50 }} label="Code/Réf" onChange={(event) => updateForm("outil_code", event.target.value)} value={form.outil_code} />
        <MetadataField disabled={!editing || saving} label="Version" onChange={(event) => updateForm("version", event.target.value)} type="number" value={form.version} />
        <UserField disabled={!editing || saving || loadingUsers} fallbackLabel={userFallbacks.redacteur_id} label="Rédigé par" onChange={(value) => updateForm("redacteur_id", value)} options={users} value={form.redacteur_id} />
        <MetadataField disabled={!editing || saving} InputLabelProps={{ shrink: true }} label="Date rédaction" onChange={(event) => updateForm("date_redaction", event.target.value)} type="date" value={form.date_redaction} />
        <UserField disabled={!editing || saving || loadingUsers} fallbackLabel={userFallbacks.verificateur_id} label="Vérifié par" onChange={(value) => updateForm("verificateur_id", value)} options={users} value={form.verificateur_id} />
        <UserField disabled={!editing || saving || loadingUsers} fallbackLabel={userFallbacks.approbateur_id} label="Approuvé par" onChange={(value) => updateForm("approbateur_id", value)} options={users} value={form.approbateur_id} />
      </Box>

      <input hidden onChange={selectFile} ref={fileInputRef} type="file" />
    </Paper>
  );
};

export default OutilEntryPanel;
